import { memo, useEffect } from 'react';
import { Box, Image, Loader, ScrollView } from '@bonanza-social/moonlight';
import PromoSummary from '../PromoSummary/PromoSummary';
import PromoTerms from '../PromoTerms/PromoTerms';
import usePromo from '../../hooks/usePromo';
import { PromoDetailsEmpty } from '../PromosEmptyStates';
import { Promotion } from '../../state/promos/promos.types';
import ReferrerPromoDetails from './ReferrerPromoDetails';
import {
  selectAuthenticationStatus,
  useDispatch,
  useSelector,
} from '@bonanza-social/toolkit/state';
import { promoNotFound } from '../../state/promos/promos.slice';

function PromoDetails({
  id,
}: {
  id: Promotion['slug'] | Promotion['promotionId'];
}) {
  const dispatch = useDispatch();
  const authStatus = useSelector(selectAuthenticationStatus);
  const { promo, promosDataState } = usePromo(id);

  useEffect(() => {
    const isPromoInvalid = !promo || promosDataState === 'rejected';
    const isPromosLoaded = promosDataState !== 'pending' && promosDataState !== 'idle';
    const isAuthenticated = authStatus === 'authenticated';

    if (isPromoInvalid && isPromosLoaded && isAuthenticated) {
      dispatch(promoNotFound());
    }
  }, [promo, promosDataState, authStatus, dispatch]);

  if (promosDataState === 'pending') {
    return <Loader />;
  }

  if (!promo || promosDataState === 'rejected') {
    if (authStatus === 'unauthenticated') {
      return <PromoDetailsEmpty />;
    } else {
      return null;
    }
  }

  if (promo.contentType === 'Referrer') {
    return <ReferrerPromoDetails promo={promo} />;
  }

  return (
    <ScrollView
      contentContainerStyle={{
        paddingBottom: 'safe-area-inset-bottom',
        width: '100%',
        maxWidth: 600,
        marginHorizontal: 'auto',
      }}
      showsHorizontalScrollIndicator={false}
    >
      <Box>
        <Box flex={1}>
          <Image
            source={promo?.imageUrl}
            width="100%"
            contentFit="cover"
            aspectRatio={2}
            borderRadius={{
              mobile: undefined,
              tablet: 'radius-400',
              desktop: 'radius-400',
            }}
          />
        </Box>
        <Box gap="space-200" padding="space-400">
          <PromoSummary promo={promo} />
          <PromoTerms
            description={promo.longDescription}
            termsAndConditions={promo.termsAndConditions}
            promoType={promo.promoType}
          />
        </Box>
      </Box>
    </ScrollView>
  );
}

export default memo(PromoDetails);
